import useCurrentClinic from '../../hooks/useCurrentClinic';
import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IIntakeData, INewPatientData } from './types';
import axios from 'axios';
import useStores from '../../hooks/useStores';
import {
  DEFAULT_RETRY_DELAY,
  DEFAULT_RETRY_NUMBERS,
} from '../../config/query-client';
import { runInAction } from 'mobx';

export default function useIntakeData() {
  const queryClient = useQueryClient();
  const { clinic } = useCurrentClinic();
  const {
    intakeDataStore,
    adminDashboardStore,
    patientsServiceStore: { queryKey },
  } = useStores();
  const intakeQueryKey = useMemo(
    () => ['camel', 'intake', 'init_data', { clinic_id: clinic?.uuid }],
    [clinic]
  );

  const { data, isLoading, isError } = useQuery<IIntakeData>({
    queryKey: intakeQueryKey,
    enabled: !!clinic?.uuid,
    retry: DEFAULT_RETRY_NUMBERS,
    retryDelay: DEFAULT_RETRY_DELAY,
  });

  useEffect(() => {
    runInAction(() => {
      adminDashboardStore.typeOfCare = data?.tocOptions ?? [];
      adminDashboardStore.careLocations = data?.locations ?? [];
    });
  }, [data]);

  const createPatientAction = useMutation({
    mutationFn: async (patient: Partial<INewPatientData>) => {
      const result = await axios.post('api/camel/intake/create/', patient);
      invalidatePatientsQuery();
      return result?.data?.uuid ?? '';
    },
  });

  const invalidatePatientsQuery = async () => {
    await queryClient.invalidateQueries({ queryKey });
  };

  return {
    data,
    isLoading: !!clinic?.uuid && isLoading,
    isError,
    createPatient: createPatientAction.mutateAsync,
    state: intakeDataStore,
  };
}
